import {
  useState,
  useCallback,
  useRef,
  useEffect,
  createContext,
  useContext,
} from "react";
import { createPortal } from "react-dom";
import {
  Check,
  Info,
  AlertTriangle,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  InfoIcon,
  XCircle,
} from "lucide-react";

export const ToastType = {
  SUCCESS: "success",
  ERROR: "error",
  WARNING: "warning",
  INFO: "info",
  LOADING: "loading",
};

export const ToastPosition = {
  BOTTOM_CENTER_ABOVE: "bottom-center-above",
  TOP_RIGHT: "top-right",
  TOP_LEFT: "top-left",
  TOP_CENTER: "top-center",
  BOTTOM_RIGHT: "bottom-right",
  BOTTOM_LEFT: "bottom-left",
  BOTTOM_CENTER: "bottom-center",
};

const DEFAULT_CONFIG = {
  duration: 5000,
  position: ToastPosition.BOTTOM_CENTER_ABOVE,
  maxToasts: 3,
};

// Create a context for the toast
const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

// Toast Component - Modern Design
const ToastItem = ({ toast, onDismiss }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const timerRef = useRef(null);
  const progressRef = useRef(null);

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(toast.id);
    }, 300);
  }, [toast.id, onDismiss]);

  // Auto dismiss with progress bar
  useEffect(() => {
    if (toast.duration > 0 && toast.type !== ToastType.LOADING) {
      const startTime = Date.now();
      const totalDuration = toast.duration;

      // Progress animation
      progressRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / totalDuration) * 100);
        setProgress(remaining);
      }, 50);

      // Dismiss timer
      timerRef.current = setTimeout(() => {
        handleDismiss();
      }, totalDuration);

      return () => {
        if (progressRef.current) {
          clearInterval(progressRef.current);
        }
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    }
  }, [toast.duration, toast.type, handleDismiss]);

  const getTypeStyles = () => {
    const baseStyles = "rounded-2xl border shadow-2xl backdrop-blur-md";
    const typeStyles = {
      [ToastType.SUCCESS]:
        "bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200/80 text-emerald-800 dark:from-emerald-900/20 dark:to-emerald-950/30 dark:border-emerald-800/30 dark:text-emerald-300",
      [ToastType.ERROR]:
        "bg-gradient-to-br from-rose-50 to-rose-100/50 border-rose-200/80 text-rose-800 dark:from-rose-900/20 dark:to-rose-950/30 dark:border-rose-800/30 dark:text-rose-300",
      [ToastType.WARNING]:
        "bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200/80 text-amber-800 dark:from-amber-900/20 dark:to-amber-950/30 dark:border-amber-800/30 dark:text-amber-300",
      [ToastType.INFO]:
        "bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200/80 text-blue-800 dark:from-blue-900/20 dark:to-blue-950/30 dark:border-blue-800/30 dark:text-blue-300",
      [ToastType.LOADING]:
        "bg-gradient-to-br from-slate-50 to-slate-100/50 border-slate-200/80 text-slate-800 dark:from-slate-900/20 dark:to-slate-950/30 dark:border-slate-800/30 dark:text-slate-300",
    };
    return `${baseStyles} ${typeStyles[toast.type] || typeStyles[ToastType.INFO]}`;
  };

  const getIcon = () => {
    const iconSize = "w-6 h-6";
    const icons = {
      [ToastType.SUCCESS]: (
        <CheckCircle
          className={`${iconSize} text-emerald-500 dark:text-emerald-400`}
        />
      ),
      [ToastType.ERROR]: (
        <XCircle className={`${iconSize} text-rose-500 dark:text-rose-400`} />
      ),
      [ToastType.WARNING]: (
        <AlertCircle
          className={`${iconSize} text-amber-500 dark:text-amber-400`}
        />
      ),
      [ToastType.INFO]: (
        <InfoIcon className={`${iconSize} text-blue-500 dark:text-blue-400`} />
      ),
      [ToastType.LOADING]: (
        <Loader2
          className={`${iconSize} text-slate-500 dark:text-slate-400 animate-spin`}
        />
      ),
    };
    return toast.icon || icons[toast.type] || icons[ToastType.INFO];
  };

  const getIconContainerStyles = () => {
    const baseStyles = "p-3 rounded-xl backdrop-blur-sm";
    const typeStyles = {
      [ToastType.SUCCESS]: "bg-emerald-500/10",
      [ToastType.ERROR]: "bg-rose-500/10",
      [ToastType.WARNING]: "bg-amber-500/10",
      [ToastType.INFO]: "bg-blue-500/10",
      [ToastType.LOADING]: "bg-slate-500/10",
    };
    return `${baseStyles} ${typeStyles[toast.type] || typeStyles[ToastType.INFO]}`;
  };

  return (
    <div
      className={`flex items-start gap-4 p-5 transform transition-all duration-300 ${
        isExiting ? "scale-95 opacity-0" : "scale-100 opacity-100"
      } ${getTypeStyles()}`}
      style={{
        animation: !isExiting
          ? "slideUpFade 0.4s cubic-bezier(0.16, 1, 0.3, 1)"
          : "slideDownFade 0.3s ease-out",
        minWidth: "320px",
        maxWidth: "400px",
      }}
    >
      {/* Icon Container */}
      <div className={`${getIconContainerStyles()} flex-shrink-0`}>
        {getIcon()}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <p className="font-semibold text-sm leading-tight">
              {toast.message}
            </p>
            {toast.description && (
              <p className="text-xs opacity-80 mt-1">{toast.description}</p>
            )}
          </div>
          {toast.type !== ToastType.LOADING && (
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 p-1.5 hover:bg-black/5 dark:hover:bg-white/10 rounded-full transition-all duration-200 group"
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 transition-opacity" />
            </button>
          )}
        </div>

        {/* Progress Bar - only for auto-dismiss toasts */}
        {toast.duration > 0 && toast.type !== ToastType.LOADING && (
          <div className="h-0.5 w-full bg-black/5 dark:bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-current opacity-30 transition-all duration-100"
              style={{
                width: `${progress}%`,
                transition: "width 50ms linear",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Toast Container Component - Centered above bottom half
const ToastContainer = ({
  toasts,
  dismissToast,
  position = ToastPosition.BOTTOM_CENTER_ABOVE,
}) => {
  const getPositionStyles = () => {
    switch (position) {
      case ToastPosition.BOTTOM_CENTER_ABOVE:
        return "bottom-2 left-1/2 -translate-x-1/2 items-center";
      case ToastPosition.TOP_LEFT:
        return "top-4 left-4 items-start";
      case ToastPosition.TOP_CENTER:
        return "top-4 left-1/2 -translate-x-1/2 items-center";
      case ToastPosition.TOP_RIGHT:
        return "top-4 right-4 items-end";
      case ToastPosition.BOTTOM_LEFT:
        return "bottom-4 left-4 items-start";
      case ToastPosition.BOTTOM_CENTER:
        return "bottom-4 left-1/2 -translate-x-1/2 items-center";
      case ToastPosition.BOTTOM_RIGHT:
        return "bottom-4 right-4 items-end";
      default:
        return "bottom-1/3 left-1/2 -translate-x-1/2 items-center";
    }
  };

  if (toasts.length === 0) return null;

  return createPortal(
    <div
      className={`fixed z-[9999] flex flex-col gap-4 ${getPositionStyles()}`}
      style={{
        pointerEvents: "none",
      }}
    >
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          style={{
            pointerEvents: "auto",
            transform: `translateY(${index * -10}px)`,
            opacity: 1 - index * 0.2,
            zIndex: toasts.length - index,
          }}
          className="transition-all duration-300 ease-out"
        >
          <ToastItem toast={toast} onDismiss={dismissToast} />
        </div>
      ))}
    </div>,
    document.getElementById("toast-root") || document.body,
  );
};

// Toast Provider Component
export const ToastProvider = ({ children, config = {} }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});
  const progressTimersRef = useRef({});
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
      Object.values(progressTimersRef.current).forEach(clearInterval);
    };
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    if (progressTimersRef.current[id]) {
      clearInterval(progressTimersRef.current[id]);
      delete progressTimersRef.current[id];
    }
  }, []);

  const showToast = useCallback(
    (message, type = ToastType.INFO, options = {}) => {
      const id =
        Date.now().toString() + Math.random().toString(36).substr(2, 9);
      const duration =
        type === ToastType.LOADING
          ? 0
          : options.duration || mergedConfig.duration;

      const newToast = {
        id,
        message,
        type,
        duration,
        description: options.description,
        icon: options.icon,
        createdAt: Date.now(),
        ...options,
      };

      setToasts((prev) => {
        const updatedToasts = [...prev, newToast];
        if (updatedToasts.length > mergedConfig.maxToasts) {
          // Remove the oldest toast
          const [oldest, ...rest] = updatedToasts;
          dismissToast(oldest.id);
          return rest;
        }
        return updatedToasts;
      });

      if (duration > 0 && type !== ToastType.LOADING) {
        timersRef.current[id] = setTimeout(() => {
          dismissToast(id);
        }, duration);
      }

      return id;
    },
    [mergedConfig.duration, mergedConfig.maxToasts, dismissToast],
  );

  const dismissAllToasts = useCallback(() => {
    setToasts([]);
    Object.values(timersRef.current).forEach(clearTimeout);
    Object.values(progressTimersRef.current).forEach(clearInterval);
    timersRef.current = {};
    progressTimersRef.current = {};
  }, []);

  const updateToast = useCallback((id, updates) => {
    setToasts((prev) =>
      prev.map((toast) => (toast.id === id ? { ...toast, ...updates } : toast)),
    );
  }, []);

  const loading = useCallback(
    (message, options = {}) => {
      return showToast(message, ToastType.LOADING, options);
    },
    [showToast],
  );

  const promise = useCallback(
    async (promise, messages) => {
      const loadingId = loading(messages.loading || "جاري المعالجة...");

      try {
        const result = await promise;
        dismissToast(loadingId);
        showToast(messages.success || "تمت العملية بنجاح", ToastType.SUCCESS);
        return result;
      } catch (error) {
        dismissToast(loadingId);
        showToast(
          messages.error || error.message || "حدث خطأ",
          ToastType.ERROR,
        );
        throw error;
      }
    },
    [loading, showToast, dismissToast],
  );

  const contextValue = {
    toasts,
    showToast,
    dismissToast,
    dismissAllToasts,
    updateToast,
    loading,
    promise,
    success: (message, options) =>
      showToast(message, ToastType.SUCCESS, options),
    error: (message, options) => showToast(message, ToastType.ERROR, options),
    warning: (message, options) =>
      showToast(message, ToastType.WARNING, options),
    info: (message, options) => showToast(message, ToastType.INFO, options),
    ToastType,
    ToastPosition,
    config: mergedConfig,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer
        toasts={toasts}
        dismissToast={dismissToast}
        position={mergedConfig.position}
      />
    </ToastContext.Provider>
  );
};
